import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { formatPhone } from "@/lib/utils";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { API_BASE_URL, api, assetUrl } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import type { User } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BuyerPreferencesModal from "@/components/BuyerPreferencesModal";
import BuyerProfileCard from "@/components/BuyerProfileCard";
import {
  ArrowRight,
  Bell,
  CalendarClock,
  Camera,
  Check,
  Eye,
  EyeOff,
  FileText,
  Lock,
  Mail,
  Pencil,
  PenLine,
  Phone,
  Settings as SettingsIcon,
  Shield,
  User as UserIcon,
  UserPlus,
  X,
} from "lucide-react";
import { MetricTile, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import { TierManagementCard } from "@/components/TierManagementCard";
import { PaymentMethodCard } from "@/components/PaymentMethodCard";
import { PaymentHistoryCard } from "@/components/PaymentHistoryCard";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { PASSWORD_MIN_LENGTH, firstPasswordIssue, isPasswordStrong } from "@/lib/password";
import { nameIssue, phoneIssue } from "@/lib/profileValidation";
import { orgScopedPath } from "@/lib/orgDoor";
import { PORTAL_ADMIN_EMAIL } from "@/lib/access";

// Settings tabs — each is also a sidebar child link (/<role>/settings/:tab).
const TAB_LABELS = {
  profile: "My Profile",
  security: "Password & Security",
  notifications: "Email Notifications",
  subscription: "Subscription Plan",
  payment: "Payment Method",
  history: "Payment History",
  documents: "Documents",
} as const;

type TabId = keyof typeof TAB_LABELS;

/** Which door gets which tabs. Buyers, advisors and admins all get the same
 *  Account Settings chrome; the tab SET differs because only buyers hold a
 *  subscription, and the notification toggle is specifically about listing
 *  matches. Everything else applies to every role. */
const SURFACE_TABS = {
  buyer: ["profile", "security", "notifications", "subscription", "payment", "history", "documents"],
  advisor: ["profile", "security", "documents"],
  admin: ["profile", "security", "documents"],
} as const satisfies Record<string, readonly TabId[]>;

type Surface = keyof typeof SURFACE_TABS;

const SURFACE_HERO_COPY: Record<Surface, string> = {
  buyer: "Manage your profile, password, notifications, subscription, payment method, payment history and documents — all in one place.",
  advisor: "Manage your profile, listing photo and bio, password and documents — all in one place.",
  admin: "Manage your profile, password and documents — all in one place.",
};

/** Matches the backend AdvisorProfileUpdateRequest cap. */
const ADVISOR_BIO_MAX = 1000;

const ROLE_LABELS: Record<string, string> = {
  buyer: "Buyer",
  seller: "Seller",
  bl_advisor: "BL Advisor",
  admin: "Admin",
  org_admin: "Org Admin",
};

const SURFACE_BASE_PATH: Record<Surface, string> = {
  buyer: "/buyer/settings",
  advisor: "/bl-advisor/settings",
  admin: "/admin/settings",
};

/** Sellers keep the older single-page layout, which stacked these two sections
 *  with no tab bar. Listed explicitly so the tab-less path can't silently start
 *  showing a buyer-only section. */
const LEGACY_SECTIONS: readonly TabId[] = ["profile", "security"];

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  // Which settings door we came through. Read from the PATH, not the role list:
  // an admin usually also holds other roles, and the tabs have to match the
  // shell they are actually looking at, not whichever role sorts first.
  const { pathname, search } = useLocation();
  const surface: Surface | null =
    pathname.includes("/bl-advisor/") ? "advisor"
    : pathname.includes("/admin/") ? "admin"
    : pathname.includes("/buyer/") ? "buyer"
    : null; // seller — legacy single-page layout
  const tabs: readonly TabId[] = surface ? SURFACE_TABS[surface] : [];
  // Active tab from /<role>/settings/:tab; anything unknown (or a tab this role
  // does not have) falls back to the profile tab.
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const activeTab: TabId = tabs.includes(tabParam as TabId) ? (tabParam as TabId) : "profile";
  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name ?? "");
  const [lastName, setLastName] = useState(user?.last_name ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [profileLoading, setProfileLoading] = useState(false);
  const toast = useToast();
  const [ndaOpening, setNdaOpening] = useState(false);

  // Open the user's OWN signed NDA copy (with their signature page). It's an
  // authenticated endpoint, so a plain <a href> can't send the token — fetch as
  // a blob and open it. The blank tab opens synchronously (pre-await) so the
  // browser doesn't treat the post-await open as an unrequested popup.
  const viewSignedNda = async () => {
    if (ndaOpening) return;
    setNdaOpening(true);
    const win = window.open("", "_blank");
    try {
      const blob = await api.blob("/auth/legal/my-nda.pdf");
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      win?.close();
      toast.error("Could not open your signed NDA. Please try again.");
    } finally {
      setNdaOpening(false);
    }
  };

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  // Reset-password-by-email flow (for users who forgot their current password)
  type ResetStep = "idle" | "enter-otp" | "enter-password" | "done";
  const [resetStep, setResetStep] = useState<ResetStep>("idle");
  const [resetOtp, setResetOtp] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetShowPassword, setResetShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetCooldown, setResetCooldown] = useState(0);
  const resetCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);

  const advisorPhotoRef = useRef<HTMLInputElement>(null);
  const [advisorPhotoLoading, setAdvisorPhotoLoading] = useState(false);

  const isAdvisor = user?.roles.includes("bl_advisor");
  const isBuyer = user?.roles.includes("buyer");

  const [bio, setBio] = useState("");
  const [editingBio, setEditingBio] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);

  const [emailNotifLoading, setEmailNotifLoading] = useState(false);

  const handleToggleEmailNotifications = async () => {
    if (!user) return;
    const next = !user.email_notifications_enabled;
    setEmailNotifLoading(true);
    try {
      const updated = await api.patch<User>("/profile/me/email-notifications", { enabled: next });
      updateUser(updated);
      toast.success(next ? "Email notifications enabled" : "Email notifications turned off");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update preference");
    } finally {
      setEmailNotifLoading(false);
    }
  };

  interface CategoryItem { id: number; name: string }
  const [prefCategories, setPrefCategories] = useState<CategoryItem[]>([]);
  // `?edit=buybox` (the dashboard's "Refine your buybox" link) lands with the
  // BuyBox editor already open.
  const [showPrefsModal, setShowPrefsModal] = useState(
    () => new URLSearchParams(search).get("edit") === "buybox",
  );

  // Portal-admin-only buyer provisioning
  const isPortalAdmin = user?.email === PORTAL_ADMIN_EMAIL;
  const [showCreateBuyer, setShowCreateBuyer] = useState(false);
  const [cbFirstName, setCbFirstName] = useState("");
  const [cbLastName, setCbLastName] = useState("");
  const [cbEmail, setCbEmail] = useState("");
  const [cbPhone, setCbPhone] = useState("");
  const [cbPassword, setCbPassword] = useState("");
  const [cbShowPassword, setCbShowPassword] = useState(false);
  const [cbLoading, setCbLoading] = useState(false);

  const resetCreateBuyer = () => {
    setCbFirstName(""); setCbLastName(""); setCbEmail("");
    setCbPhone(""); setCbPassword(""); setCbShowPassword(false);
  };

  const handleCreateBuyer = async (event: FormEvent) => {
    event.preventDefault();
    const cbIssue = firstPasswordIssue(cbPassword);
    if (cbIssue) {
      toast.error(cbIssue);
      return;
    }
    setCbLoading(true);
    try {
      await api.post("/admin/create-buyer", {
        email: cbEmail.trim(),
        first_name: cbFirstName.trim(),
        last_name: cbLastName.trim(),
        phone: cbPhone.trim() || null,
        password: cbPassword,
      });
      toast.success(`Buyer account created for ${cbEmail.trim()}`);
      resetCreateBuyer();
      setShowCreateBuyer(false);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to create buyer");
    } finally {
      setCbLoading(false);
    }
  };

  useEffect(() => {
    if (!isBuyer) return;
    api.get<CategoryItem[]>("/lookups/categories").then(setPrefCategories).catch(() => {});
  }, [isBuyer]);

  useEffect(() => () => {
    if (resetCooldownRef.current) clearInterval(resetCooldownRef.current);
  }, []);

  useEffect(() => {
    if (!isAdvisor) return;
    api.get<{ bio: string | null }>("/profile/me/advisor")
      .then((d) => setBio(d.bio ?? ""))
      .catch(() => {});
  }, [isAdvisor]);

  const profileCompletion = useMemo(() => {
    const checks = [
      Boolean(user?.first_name),
      Boolean(user?.last_name),
      Boolean(user?.email),
      Boolean(user?.phone),
      Boolean(user?.avatar_url),
      ...(surface === "advisor" ? [Boolean(bio.trim())] : []),
    ];
    const completed = checks.filter(Boolean).length;
    return Math.round((completed / checks.length) * 100);
  }, [user?.first_name, user?.last_name, user?.email, user?.phone, user?.avatar_url, surface, bio]);

  if (!user) return null;

  const handleProfileSave = async (event: FormEvent) => {
    event.preventDefault();
    setProfileLoading(true);
    const profileIssue =
      nameIssue(firstName, "First name") ?? nameIssue(lastName, "Last name") ?? phoneIssue(phone);
    if (profileIssue) {
      toast.error(profileIssue);
      setProfileLoading(false);
      return;
    }
    try {
      const updated = await api.patch<User>("/profile/me", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone: phone.trim() || null,
      });
      updateUser(updated);
      setEditing(false);
      toast.success("Profile updated");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Update failed");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (event: FormEvent) => {
    event.preventDefault();
    const issue = firstPasswordIssue(newPassword);
    if (issue) {
      toast.error(issue);
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must be different from your current password");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setPwLoading(true);
    try {
      await api.post("/profile/me/password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password changed successfully");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  const startResetCooldown = () => {
    setResetCooldown(60);
    if (resetCooldownRef.current) clearInterval(resetCooldownRef.current);
    resetCooldownRef.current = setInterval(() => {
      setResetCooldown((prev) => {
        if (prev <= 1) { clearInterval(resetCooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResetSendOtp = async () => {
    const email = user?.email?.trim();
    if (!email) return;
    setResetLoading(true);
    setResetError("");
    try {
      await api.post("/auth/forgot-password/send-otp", { email });
      setResetStep("enter-otp");
      setResetOtp("");
      startResetCooldown();
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : "Failed to send code");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const resetIssue = firstPasswordIssue(resetNewPassword);
    if (resetIssue) { setResetError(resetIssue); return; }
    if (resetNewPassword !== resetConfirmPassword) { setResetError("Passwords do not match"); return; }
    setResetLoading(true);
    setResetError("");
    try {
      await api.post("/auth/forgot-password/reset", {
        email: user?.email?.trim(),
        otp_code: resetOtp.trim(),
        new_password: resetNewPassword,
      });
      setResetStep("done");
    } catch (err: unknown) {
      setResetError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetFlow = () => {
    setResetStep("idle");
    setResetOtp("");
    setResetNewPassword("");
    setResetConfirmPassword("");
    setResetShowPassword(false);
    setResetError("");
    setResetCooldown(0);
    if (resetCooldownRef.current) clearInterval(resetCooldownRef.current);
  };

  const handleAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 2 MB.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }

    setAvatarLoading(true);
    try {
      const updated = await api.upload<User>("/profile/me/avatar", file);
      updateUser(updated);
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Avatar upload failed");
    } finally {
      setAvatarLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleAdvisorPhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image too large. Maximum size is 2 MB.");
      return;
    }

    setAdvisorPhotoLoading(true);
    try {
      const updated = await api.upload<User>("/profile/me/avatar", file);
      updateUser(updated);
      toast.success("Profile photo updated");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Photo upload failed");
    } finally {
      setAdvisorPhotoLoading(false);
      if (advisorPhotoRef.current) advisorPhotoRef.current.value = "";
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    setFirstName(user.first_name);
    setLastName(user.last_name);
    setPhone(user.phone ?? "");
  };

  const handleBioSave = async (event: FormEvent) => {
    event.preventDefault();
    setBioLoading(true);
    try {
      const updated = await api.patch<{ bio: string | null }>("/profile/me/advisor", { bio: bio.trim() || null });
      setBio(updated.bio ?? "");
      setEditingBio(false);
      toast.success("Bio updated");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to update bio");
    } finally {
      setBioLoading(false);
    }
  };

  const firstError = editing ? nameIssue(firstName, "First name") : null;
  const lastError = editing ? nameIssue(lastName, "Last name") : null;
  const phoneError = editing ? phoneIssue(phone) : null;
  const detailsInvalid = !!(firstError || lastError || phoneError);

  const initials = `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "U";
  const statusLabel = user.status.replace(/_/g, " ");
  const statusClasses =
    user.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300";
  const memberSince = new Date(user.created_at).toLocaleDateString(undefined, {
    month: "short",
    year: "numeric",
  });

  const usesTabs = surface !== null;
  /* Collapses the two layouts into one condition: with tabs a section shows
     only on its own tab, while the legacy seller page stacks everything it
     had. */
  const showSection = (tab: TabId) =>
    usesTabs ? activeTab === tab : LEGACY_SECTIONS.includes(tab);

  return (
    <PageShell className="space-y-7">
      {!usesTabs && (
      <PageHero
        eyebrow="My Profile"
        title="Keep your account identity and security sharp from one polished control room."
        description="The profile area now matches the premium interior style, making personal details, role visibility, and password maintenance clearer and faster to manage."
        actions={
          <>
            <Button size="lg" className="rounded-full" onClick={() => setEditing(true)} disabled={editing}>
              <Pencil className="h-4 w-4" />
              {editing ? "Editing enabled" : "Edit profile"}
            </Button>
            <Link to="/listings/mine">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white"
              >
                My listings
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            {isPortalAdmin && (
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white"
                onClick={() => setShowCreateBuyer(true)}
              >
                <UserPlus className="h-4 w-4" />
                Create buyer
              </Button>
            )}
          </>
        }
        aside={
          <>
            <div className="flex items-start gap-4">
              <div className="group relative h-22 w-22 overflow-hidden rounded-[1.3rem] border border-border/60 bg-muted">
                {user.avatar_url ? (
                  <img src={assetUrl(user.avatar_url)!} alt="Avatar" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,hsl(41_65%_52%/0.28),hsl(38_45%_28%/0.4))] text-2xl font-black text-primary">
                    {initials}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={avatarLoading}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                >
                  {avatarLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                </button>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                  onChange={handleAvatarChange}
                />
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.2em] text-muted-foreground">Account holder</div>
                <div className="mt-1 truncate text-xl font-black tracking-tight text-foreground">
                  {user.first_name} {user.last_name}
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="truncate">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="mt-1.5 flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    <span>{formatPhone(user.phone)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {user.roles.map((role) => (
                <span
                  key={role}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-[0.68rem] font-bold uppercase tracking-[0.15em] text-primary"
                >
                  <Shield className="h-3.5 w-3.5" />
                  {role}
                </span>
              ))}
            </div>

            <div className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.68rem] font-bold uppercase tracking-[0.15em] ${statusClasses}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${user.status === "active" ? "bg-emerald-600" : "bg-amber-500"}`} />
              {statusLabel}
            </div>
          </>
        }
      />
      )}

      {/* ── Settings header + tab bar — same Dark · Gold hero as the other
          interior pages (Deal CRM, SBA Calculator, AI Deal Scoring). Shared by
          the buyer, BL advisor and admin doors so the account area looks like
          one product; the tab set comes from SURFACE_TABS. ── */}
      {usesTabs && surface && (
        <div className="space-y-4">
          <section className="public-hero-shell rounded-[1.85rem] px-6 py-7 shadow-glow sm:px-8">
            <div className="public-grid-lattice absolute inset-0 opacity-45" />
            <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
            <div className="relative z-10">
              <div className="public-pill">
                <SettingsIcon className="h-3.5 w-3.5" />
                Account
              </div>
              <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
                Account <span className="text-gradient">Settings</span>
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                {SURFACE_HERO_COPY[surface]}
              </p>
            </div>
          </section>
          <div className="inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-2xl border border-border/70 bg-card p-1.5 shadow-sm">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                // orgScopedPath: an ECFA user lives under /<slug>/…, and a bare
                // path here would drop them out of their org door.
                onClick={() => navigate(orgScopedPath(`${SURFACE_BASE_PATH[surface]}/${t}`, user.org_slug))}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  activeTab === t
                    ? "bg-primary text-primary-foreground shadow"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* The buyer's profile tab is their BuyBox; every other role gets the
          identity tiles + personal details in the same slot. */}
      {surface === "buyer" && showSection("profile") && (
        <PagePanel>
          <BuyerProfileCard categories={prefCategories} onEdit={() => setShowPrefsModal(true)} />
        </PagePanel>
      )}

      {surface !== "buyer" && showSection("profile") && (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricTile label="Profile Completion" value={`${profileCompletion}%`} icon={UserIcon} tone="primary" />
          <MetricTile label="Account Status" value={statusLabel.replace(/^\w/, (c) => c.toUpperCase())} icon={Shield} tone={user.status === "active" ? "emerald" : "amber"} />
          <MetricTile label="Email Verified" value={user.email_verified ? "Yes" : "No"} icon={Mail} tone={user.email_verified ? "emerald" : "amber"} />
          <MetricTile label="Member Since" value={memberSince} icon={CalendarClock} tone="rose" />
        </section>
      )}

      {surface !== "buyer" && showSection("profile") && (
      <PagePanel>
        <PanelHeading
          title="Personal information"
          description="Update your name and phone details. Changes are applied directly to your account profile."
          actions={
            !editing ? (
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" />
                Edit details
              </Button>
            ) : null
          }
        />

        {editing ? (
          <form onSubmit={handleProfileSave} className="space-y-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  First Name
                </Label>
                <Input id="firstName" value={firstName} onChange={(event) => setFirstName(event.target.value)} required maxLength={60} />
                {firstName.trim() !== "" && firstError && (
                  <p className="text-xs font-medium text-destructive">{firstError}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Last Name
                </Label>
                <Input id="lastName" value={lastName} onChange={(event) => setLastName(event.target.value)} required maxLength={60} />
                {lastName.trim() !== "" && lastError && (
                  <p className="text-xs font-medium text-destructive">{lastError}</p>
                )}
              </div>
            </div>

            <div className="max-w-md space-y-2">
              <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                maxLength={20}
              />
              {phoneError && <p className="text-xs font-medium text-destructive">{phoneError}</p>}
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button type="submit" disabled={profileLoading || detailsInvalid} className="rounded-full">
                <Check className="h-4 w-4" />
                {profileLoading ? "Saving..." : "Save changes"}
              </Button>
              <Button type="button" variant="outline" className="rounded-full" onClick={cancelEdit}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">First Name</div>
              <div className="mt-2 text-sm font-semibold text-foreground">{user.first_name}</div>
            </div>
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">Last Name</div>
              <div className="mt-2 text-sm font-semibold text-foreground">{user.last_name}</div>
            </div>
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">Email</div>
              <div className="mt-2 text-sm font-semibold text-foreground">{user.email}</div>
            </div>
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">Phone</div>
              <div className="mt-2 text-sm font-semibold text-foreground">{formatPhone(user.phone) || "Not provided"}</div>
            </div>
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4 sm:col-span-2">
              <div className="text-[0.68rem] font-bold uppercase tracking-[0.15em] text-muted-foreground">Roles</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {user.roles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary"
                  >
                    <Shield className="h-3 w-3" />
                    {ROLE_LABELS[role] ?? role}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </PagePanel>
      )}

      {surface === "admin" && isPortalAdmin && showSection("profile") && (
        <PagePanel>
          <PanelHeading
            title="Create buyer account"
            description="Provision a buyer directly — no email verification required."
            actions={
              <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setShowCreateBuyer(true)}>
                <UserPlus className="h-3.5 w-3.5" />
                Create buyer
              </Button>
            }
          />
        </PagePanel>
      )}

      {showSection("security") && (
      <PagePanel>
        <PanelHeading
          title="Password and security"
          description="Set a new password to keep your account secure. It must meet all the requirements below."
        />

        <form onSubmit={handlePasswordChange} className="space-y-5">
          <div className="max-w-xl space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currentPw" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Current Password
              </Label>
              <div className="relative">
                <Input
                  id="currentPw"
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPw" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="newPw"
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <PasswordRequirements password={newPassword} className="pt-1" />
              {newPassword && currentPassword && newPassword === currentPassword && (
                <p className="text-xs font-medium text-destructive">
                  New password must be different from your current password.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPw" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Confirm New Password
              </Label>
              <Input
                id="confirmPw"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs font-medium text-destructive">Passwords do not match.</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Button
              type="submit"
              disabled={
                pwLoading ||
                !currentPassword ||
                !isPasswordStrong(newPassword) ||
                newPassword !== confirmPassword ||
                newPassword === currentPassword
              }
              className="rounded-full"
            >
              <Lock className="h-4 w-4" />
              {pwLoading ? "Updating..." : "Update password"}
            </Button>
            <button
              type="button"
              onClick={handleResetSendOtp}
              disabled={resetLoading}
              className="text-sm font-semibold text-primary underline-offset-4 transition-colors duration-200 hover:underline disabled:opacity-60"
            >
              {resetLoading && resetStep === "idle" ? "Sending code..." : "Forgot current password? Reset via email"}
            </button>
          </div>
        </form>
      </PagePanel>
      )}

      {/* ── Reset-password-by-email modal overlay ── */}
      {resetStep !== "idle" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md space-y-5 rounded-2xl border border-border/80 bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">Reset your password</h3>
              <button type="button" onClick={closeResetFlow} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            {resetStep === "enter-otp" && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  A 6-digit code was sent to <strong>{user.email}</strong>. Enter it below.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="resetOtp">6-digit code</Label>
                  <Input
                    id="resetOtp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={resetOtp}
                    onChange={(e) => { setResetOtp(e.target.value.replace(/\D/g, "")); setResetError(""); }}
                    className="text-center text-lg font-mono tracking-widest"
                    autoFocus
                  />
                </div>
                {resetError && <p className="text-xs font-medium text-destructive">{resetError}</p>}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    className="flex-1 rounded-full"
                    onClick={() => {
                      if (resetOtp.length !== 6) { setResetError("Enter the 6-digit code"); return; }
                      setResetError("");
                      setResetStep("enter-password");
                    }}
                    disabled={resetOtp.length !== 6}
                  >
                    Next
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    onClick={handleResetSendOtp}
                    disabled={resetLoading || resetCooldown > 0}
                  >
                    {resetCooldown > 0 ? `Resend in ${resetCooldown}s` : "Resend"}
                  </Button>
                </div>
              </div>
            )}

            {resetStep === "enter-password" && (
              <form onSubmit={handleResetSubmit} className="space-y-4">
                <p className="text-sm text-muted-foreground">Set your new password. It must meet all the requirements below.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="resetNewPw">New password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="resetNewPw"
                      type={resetShowPassword ? "text" : "password"}
                      value={resetNewPassword}
                      onChange={(e) => { setResetNewPassword(e.target.value); setResetError(""); }}
                      className="pl-10 pr-10"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                      autoFocus
                    />
                    <button type="button" onClick={() => setResetShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {resetShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <PasswordRequirements password={resetNewPassword} className="pt-1" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="resetConfirmPw">Confirm new password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
                    <Input
                      id="resetConfirmPw"
                      type="password"
                      value={resetConfirmPassword}
                      onChange={(e) => { setResetConfirmPassword(e.target.value); setResetError(""); }}
                      className="pl-10"
                      required
                      minLength={PASSWORD_MIN_LENGTH}
                    />
                  </div>
                  {resetConfirmPassword && resetNewPassword !== resetConfirmPassword && (
                    <p className="text-xs font-medium text-destructive">Passwords do not match.</p>
                  )}
                </div>
                {resetError && <p className="text-xs font-medium text-destructive">{resetError}</p>}
                <Button
                  type="submit"
                  className="w-full rounded-full"
                  disabled={resetLoading || !isPasswordStrong(resetNewPassword) || resetNewPassword !== resetConfirmPassword}
                >
                  <Lock className="h-4 w-4" />
                  {resetLoading ? "Resetting..." : "Reset password"}
                </Button>
              </form>
            )}

            {resetStep === "done" && (
              <div className="space-y-4 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
                  <Check className="h-7 w-7 text-emerald-300" />
                </div>
                <p className="text-sm font-semibold text-foreground">Password reset successfully!</p>
                <p className="text-xs text-muted-foreground">You can now sign in with your new password.</p>
                <Button type="button" className="w-full rounded-full" onClick={closeResetFlow}>
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {showSection("notifications") && (
        <PagePanel>
          <PanelHeading
            title="Email notifications"
            description="Control whether we email you when a new listing matches your interests. This only affects listing emails — account, security, and support messages are always sent."
          />
          <div className="flex items-center justify-between gap-4 rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-foreground">New listing notifications</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {user.email_notifications_enabled
                    ? "You're receiving emails about new matching listings."
                    : "You've unsubscribed — turn this on to start receiving them again."}
                </div>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={user.email_notifications_enabled}
              aria-label="Toggle new listing email notifications"
              onClick={handleToggleEmailNotifications}
              disabled={emailNotifLoading}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-60 ${
                user.email_notifications_enabled ? "bg-primary" : "bg-muted-foreground/30"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  user.email_notifications_enabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        </PagePanel>
      )}

      {showSection("subscription") && (
        <PagePanel>
          <PanelHeading
            title="Subscription plan"
            description="Manage your subscription tier and upgrade to access more opportunities."
          />
          <TierManagementCard />
        </PagePanel>
      )}

      {showSection("payment") && (
        <PagePanel>
          <PanelHeading
            title="Payment method"
            description="The card we bill for your subscription and one-click listing unlocks."
          />
          <PaymentMethodCard />
        </PagePanel>
      )}

      {showSection("history") && (
        <PagePanel>
          <PanelHeading
            title="Payment history"
            description="Your successful payments — subscription charges and paid listing unlocks."
          />
          <PaymentHistoryCard />
        </PagePanel>
      )}

      {showSection("documents") && (
        <PagePanel>
          <PanelHeading
            title="Documents"
            description="Your legal documents with BusinessLocating — view or download them anytime."
          />
          <div className="space-y-3">
            {/* NDA */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">Non-Disclosure Agreement (NDA)</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    {user.nda_verified ? (
                      <>
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-600">
                          <Check className="h-3 w-3" />
                          Signed
                        </span>
                        {user.nda_signed_at && (
                          <span>
                            on {new Date(user.nda_signed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 font-semibold text-amber-600">
                        Not signed yet
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {user.nda_verified ? (
                // Signed → open THEIR copy (with the signature page) so they can
                // actually see their signature, not the blank master NDA.
                <button
                  type="button"
                  onClick={viewSignedNda}
                  disabled={ndaOpening}
                  className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:bg-accent/55 disabled:opacity-60"
                >
                  <Eye className="h-4 w-4" />
                  {ndaOpening ? "Opening…" : "View signed copy"}
                </button>
              ) : (
                // Unsigned → the signing page, not just the blank master PDF.
                // This is the only durable way in: the "sign your NDA" prompt is
                // otherwise a one-off email/notification, and once it is lost or
                // followed from a signed-out browser there is nothing in the app
                // that leads back to /sign-nda.
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`${API_BASE_URL}/auth/legal/nda.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:bg-accent/55"
                  >
                    <Eye className="h-4 w-4" />
                    View PDF
                  </a>
                  <Link
                    to="/sign-nda"
                    className="inline-flex items-center gap-2 rounded-xl border border-primary/60 bg-gold-gradient px-4 py-2 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                  >
                    <PenLine className="h-4 w-4" />
                    Review &amp; sign
                  </Link>
                </div>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground">Terms &amp; Conditions</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">Accepted when you created your account.</div>
                </div>
              </div>
              <a
                href="https://www.businesslocating.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:bg-accent/55"
              >
                <Eye className="h-4 w-4" />
                View
              </a>
            </div>
          </div>
        </PagePanel>
      )}

      <BuyerPreferencesModal
        forceOpen={showPrefsModal}
        onClose={() => {
          setShowPrefsModal(false);
          // Drop the deep-link flag so a refresh doesn't reopen the editor.
          if (new URLSearchParams(search).has("edit")) navigate(pathname, { replace: true });
        }}
      />

      {isPortalAdmin && showCreateBuyer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
          onClick={() => !cbLoading && setShowCreateBuyer(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-border/80 bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border/70">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <UserPlus className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground">Create buyer account</h2>
                  <p className="text-xs text-muted-foreground">No email verification required.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateBuyer(false)}
                disabled={cbLoading}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateBuyer} className="space-y-4 p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="cbFirst" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">First Name</Label>
                  <Input id="cbFirst" value={cbFirstName} onChange={(e) => setCbFirstName(e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cbLast" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Last Name</Label>
                  <Input id="cbLast" value={cbLastName} onChange={(e) => setCbLastName(e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cbEmail" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Email</Label>
                <Input id="cbEmail" type="email" value={cbEmail} onChange={(e) => setCbEmail(e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cbPhone" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Phone (optional)</Label>
                <Input id="cbPhone" type="tel" placeholder="+1 (555) 000-0000" value={cbPhone} onChange={(e) => setCbPhone(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cbPassword" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Password</Label>
                <div className="relative">
                  <Input id="cbPassword" type={cbShowPassword ? "text" : "password"} minLength={PASSWORD_MIN_LENGTH} value={cbPassword} onChange={(e) => setCbPassword(e.target.value)} required autoComplete="new-password" className="pr-10" />
                  <button
                    type="button"
                    onClick={() => setCbShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
                    aria-label={cbShowPassword ? "Hide password" : "Show password"}
                  >
                    {cbShowPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordRequirements password={cbPassword} className="pt-1" />
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <Button type="submit" disabled={cbLoading} className="rounded-full">
                  <Check className="h-4 w-4" />
                  {cbLoading ? "Creating..." : "Create buyer"}
                </Button>
                <Button type="button" variant="outline" className="rounded-full" disabled={cbLoading} onClick={() => setShowCreateBuyer(false)}>
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {(surface === "advisor" || surface === "admin") && showSection("profile") && (
        <PagePanel>
          <PanelHeading
            title={surface === "advisor" ? "Listing profile photo" : "Profile photo"}
            description={
              surface === "advisor"
                ? "This photo is displayed on every listing you manage so buyers can see who they're working with."
                : "Shown on your account and wherever your team sees your name."
            }
          />
          <div className="flex items-center gap-6">
            <div className="relative group shrink-0">
              <div className="h-24 w-24 rounded-2xl overflow-hidden border border-border/60 bg-muted">
                {user.avatar_url ? (
                  <img src={assetUrl(user.avatar_url)!} alt="Advisor photo" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,hsl(41_65%_52%/0.28),hsl(38_45%_28%/0.4))] text-2xl font-black text-primary">
                    {initials}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => advisorPhotoRef.current?.click()}
                  disabled={advisorPhotoLoading}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100 rounded-2xl"
                >
                  {advisorPhotoLoading ? (
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/35 border-t-white" />
                  ) : (
                    <Camera className="h-5 w-5" />
                  )}
                </button>
              </div>
              <input
                ref={advisorPhotoRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAdvisorPhotoChange}
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-semibold text-foreground">
                {user.avatar_url ? "Photo uploaded" : "No photo yet"}
              </p>
              <p className="text-xs text-muted-foreground">JPG, PNG, GIF or WebP. Max 2 MB.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl gap-1.5"
                disabled={advisorPhotoLoading}
                onClick={() => advisorPhotoRef.current?.click()}
              >
                <Camera className="h-3.5 w-3.5" />
                {advisorPhotoLoading ? "Uploading..." : user.avatar_url ? "Change photo" : "Upload photo"}
              </Button>
            </div>
          </div>
        </PagePanel>
      )}

      {surface === "advisor" && showSection("profile") && (
        <PagePanel>
          <PanelHeading
            title="Advisor bio"
            description="Write a short bio that will be shown to buyers when they view a listing you manage."
            actions={
              !editingBio ? (
                <Button variant="outline" size="sm" className="rounded-xl" onClick={() => setEditingBio(true)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Edit bio
                </Button>
              ) : null
            }
          />

          {editingBio ? (
            <form onSubmit={handleBioSave} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Bio
                </Label>
                <Textarea
                  id="bio"
                  rows={4}
                  placeholder="Tell buyers about your background, expertise, and how you can help them..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  maxLength={ADVISOR_BIO_MAX}
                  className="resize-none text-sm"
                />
                <div className="text-right text-xs text-muted-foreground">
                  {bio.length}/{ADVISOR_BIO_MAX}
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={bioLoading} className="rounded-full">
                  <Check className="h-4 w-4" />
                  {bioLoading ? "Saving..." : "Save bio"}
                </Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setEditingBio(false)}>
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="rounded-[1.3rem] border border-border/70 bg-white/70 p-4">
              <p className="text-sm text-foreground leading-relaxed">
                {bio || <span className="text-muted-foreground italic">No bio added yet. Click "Edit bio" to add one.</span>}
              </p>
            </div>
          )}
        </PagePanel>
      )}
    </PageShell>
  );
}
